import type { BeneficiariesListResponse, BeneficiaryResponse, PatchBeneficiaryPersonInput } from '@cvo/domain';
import type { PassengersCardPassenger } from '@cvo/ui/components/organisms';

/**
 * Loads the list of beneficiaries for the current user's subscription and provides helper methods to manage them.
 * @param lazy Lazy load list of beneficiaries or block navigation until resolved. Defaults true.
 * @param specificBeneficiaryId Use to retrieve one specific beneficiary reactively.
 */
export function useBeneficiaries(lazy = true, specificBeneficiaryId?: MaybeRefOrGetter<string>) {
  const api = useApi();
  const subscriptionStore = useSubscriptionStore();
  const appConfig = useAppConfig();
  const nuxtApp = useNuxtApp();
  const { t } = useI18n();

  const subscriptionId = computed(() => subscriptionStore.selectedSubscriptionId!);
  const cacheKey = computed(() => `beneficiaries-${subscriptionId.value}`);

  const getBeneficiariesAsyncData = useErrorNotification(
    { message: t('ui.error.beneficiaries') },
    useAsyncData<BeneficiariesListResponse | null>(
      cacheKey,
      () => {
        if (!appConfig.features.multiBeneficiary) {
          return Promise.resolve(null);
        }
        return api.beneficiaries.getBeneficiaries(subscriptionId.value).then(response => response.data);
      },
      {
        lazy,
        default: () => null,
        watch: [subscriptionId],
        getCachedData: alwaysGetCached,
      },
    ),
  );

  const { data: beneficiariesData, pending: isLoading, status, error: beneficiariesError, refresh } = getBeneficiariesAsyncData;

  const beneficiaries = computed<BeneficiaryResponse[]>(() => beneficiariesData.value?.beneficiaries ?? []);

  /** Applies a list update locally and to the Nuxt payload, which is what the cache serves on remount. */
  function setBeneficiaries(updatedBeneficiaries: BeneficiaryResponse[]): void {
    const updatedData = { ...beneficiariesData.value!, beneficiaries: updatedBeneficiaries };

    beneficiariesData.value = updatedData;
    nuxtApp.payload.data[cacheKey.value] = updatedData;

    // Clear the passengers cache as it may be affected by the beneficiaries update
    clearNuxtData(getPassengersCacheKey(subscriptionStore.selectedSubscriptionId!));
  }

  const maxBeneficiariesAllowed = computed<number>(() => beneficiariesData.value?.maxBeneficiariesAllowed ?? 0);

  const isLimitReached = computed(() => beneficiaries.value.length >= maxBeneficiariesAllowed.value);

  const beneficiary = computed<BeneficiaryResponse | undefined>(() => {
    return beneficiaries.value.find(benef => benef.id === toValue(specificBeneficiaryId));
  });

  /** Run PATCH beneficiary request and update data list automatically */
  async function patchBeneficiary(beneficiaryIdToUpdate: string, relationship?: string | null, person?: PatchBeneficiaryPersonInput | null) {
    const updatedBeneficiary = await api.beneficiaries.patchBeneficiary(subscriptionId.value, beneficiaryIdToUpdate, relationship, person);

    const updatedBeneficiaries = beneficiaries.value.map((benef) => (
      benef.id === beneficiaryIdToUpdate ? updatedBeneficiary.data : benef
    ));
    // Direct list update without extra request
    setBeneficiaries(updatedBeneficiaries);

    return updatedBeneficiary;
  }

  /** Run DELETE beneficiary request and update data list automatically */
  async function deleteBeneficiary(beneficiaryToDelete: PassengersCardPassenger) {
    await api.beneficiaries.deleteBeneficiary(subscriptionId.value, beneficiaryToDelete.id);

    const deletedBeneficiaryIndex = beneficiaries.value.findIndex(benef => benef.id === beneficiaryToDelete.id);
    if(beneficiariesData && deletedBeneficiaryIndex >= 0) {
      // Optimistic list update without extra request
      setBeneficiaries(beneficiaries.value.filter(benef => benef.id !== beneficiaryToDelete.id));
    } else {
      // Full request refresh
      await refresh();
    }
  }

  function clearCache(): void {
    clearNuxtData(cacheKey.value);
    clearNuxtData(getPassengersCacheKey(subscriptionStore.selectedSubscriptionId!));
  }

  return {
    beneficiaries,
    maxBeneficiariesAllowed,
    isLimitReached,
    isLoading,
    error: readonly(beneficiariesError),
    // Expose the entire async data promise/object to allow for awaiting it without making the entire composable async.
    promise: getBeneficiariesAsyncData,
    status,
    refresh,
    clearCache,

    beneficiary,
    patchBeneficiary,
    deleteBeneficiary,
  };
}
